import React, { useState, useEffect } from "react";
import AgentAvatar from "./AgentAvatar";
import MarkdownRenderer from "./MarkdownRenderer";
import { formatTokenCount } from "./helpers";

interface StreamingIndicatorProps {
  content: string;
  tokens: number;
}

export default function StreamingIndicator({ content, tokens }: StreamingIndicatorProps) {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - start);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const elapsedSec = Math.floor(elapsedMs / 1000);
  const isThinking = !content;

  return (
    <div className="flex gap-3">
      <AgentAvatar className="mt-1" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px]" style={{ color: "var(--c-text-faint)" }}>
            {isThinking ? (
              <span className="inline-flex items-center gap-1">
                <span>Thinking</span>
                <span className="thinking-dots">
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                </span>
                {elapsedSec > 0 && (
                  <span style={{ color: "var(--c-text-faint)" }}>{elapsedSec}s</span>
                )}
              </span>
            ) : (
              "Writing"
            )}
          </span>
          {tokens > 0 && (
            <span className="text-[10px]" style={{ color: "var(--c-text-secondary)" }}>
              ~{formatTokenCount(tokens)} tokens
            </span>
          )}
        </div>
        <div className="text-sm break-words" style={{ color: "var(--c-text)" }}>
          {isThinking ? (
            <span className="flex gap-1">
              <span
                className="w-1.5 h-1.5 rounded-full animate-bounce"
                style={{ backgroundColor: "var(--c-text-faint)", animationDelay: "0ms" }}
              />
              <span
                className="w-1.5 h-1.5 rounded-full animate-bounce"
                style={{ backgroundColor: "var(--c-text-faint)", animationDelay: "150ms" }}
              />
              <span
                className="w-1.5 h-1.5 rounded-full animate-bounce"
                style={{ backgroundColor: "var(--c-text-faint)", animationDelay: "300ms" }}
              />
            </span>
          ) : (
            <span>
              <MarkdownRenderer content={content} />
              <span className="streaming-cursor">|</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
